// Vendor
import React, { Fragment, useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

// Components
import Button, { ButtonColor, ButtonType, ButtonVariant } from 'src/components/atoms/Button';
import ButtonLink, {
  ButtonLinkColor,
  ButtonLinkSize,
  ButtonLinkTarget,
  ButtonLinkVariant
} from 'src/components/atoms/ButtonLink';
import { Checkbox } from 'src/components/atoms/Checkbox';
import Gap from 'src/components/atoms/Gap';
import { InputText, InputTextType } from 'src/components/atoms/InputText';
import { Line } from 'src/components/atoms/Line';
import { Row } from 'src/components/atoms/Row';
import Text, { TextColor, TextVariant } from 'src/components/atoms/Text';
import { AutoSuggest } from 'src/components/molecules/AutoSuggest';
import CopyButton from 'src/components/molecules/CopyButton';
import { ControlledDropdown, Dropdown } from 'src/components/molecules/Dropdown';
import { FileInputField } from 'src/components/molecules/FileInputField';
import { InputNumber } from 'src/components/molecules/InputNumber';
import { RadioButtonGroup } from 'src/components/molecules/RadioButtonGroup';
import { CompanyDropdown, Container } from './styles';

// Constants
import { STATES } from 'src/constants/states';

// Hooks
import useUserRoles from 'src/features/auth/hooks/useUserRoles';
import useSearchCompany from 'src/features/invitation/hooks/useSearchCompany';
import { useDebounce, useFeatureFlag, useSnackbarProvider } from 'src/hooks';

// Helpers
import {
  formatLabelValue,
  formatPhone,
  getDifferences,
  noWhiteSpacesRegex,
  onlyNumbersRegex,
  onlyRequestedSymbols,
  usZipCodeRegex,
  validateName
} from 'src/helpers';
import { getLeasingTeamLink, getPublicLink } from 'src/helpers/getDUPLink';
import { isValidSalesforceId } from 'src/helpers/salesforceValidation';

// Redux
import { selectors as propertySelectors } from 'src/features/property/propertySlice';

// Enums
import { Feature, FeatureFlagKey, FeatureState, PropertyStatus } from 'src/ts/enums';

// Types
import { RadioButtonData } from 'components/atoms/RadioButton';
import useGooglePlacesAutocomplete from 'src/features/address/hooks/useGooglePlacesAutocomplete';
import useHideDocLinks from 'src/features/fdeInbox/hooks/useHideDocLinks';
import useHidePortalLinks from 'src/features/propertyFeature/hooks/useHidePortalLinks';
import { getOldDocumentTypes } from 'src/helpers/getOldDocumentTypes';

import { Icon } from 'src/components/atoms/Icon';
import CreateOwnerModal from 'src/components/folders/modals/CreateOwnerModal';
import useOwnerList from 'src/features/owner/hooks/useOwnerList';
import { IOwner } from 'src/features/owner/ts/interfaces/Owner';
import {
  EMPTY_VALUE,
  IDENTITY_VERIFICATION_PROVIDERS,
  IDENTITY_VERIFICATION_REPORT_IMAGE_TYPES,
  IDV_FLOW_TYPE,
  IDV_FLOW_TYPES_CHECKBOXES,
  IDV_PROVIDER,
  PROPERTY_FIELD
} from 'src/features/property';
import { DEFAULT_PROPERTY_FEATURES } from 'src/features/propertyFeature';
import {
  ICompany,
  IProperty,
  IPropertyFeature,
  IPropertySupportedDoctypes,
  IValueLabel
} from 'src/ts/interfaces';

const DEFAULT_TAX_LINKING_YEARS = 2;

// AIC-1240 reparented CPN from income verification to IDV, by owner decision. CPN also
// drives the SNAX wizard step, so this controls when applicants see it. Flip back to
// Feature.INCOME_VERIFICATION to revert.
const CPN_PARENT_FEATURE = Feature.IDENTITY_VERIFICATION;

const FEATURE_LABEL_OVERRIDES: Record<string, string> = {
  [Feature.INCOME_VERIFICATION]: 'VOI',
  [Feature.OFFER_LETTER_VERIFICATION]: 'VOE',
  [Feature.ENHANCED_FINANCIAL]: 'Enhanced Financial Verification'
};

const featureLabel = (feature: IPropertyFeature) =>
  (feature.code && FEATURE_LABEL_OVERRIDES[feature.code]) || feature.name || EMPTY_VALUE;

const READ_ONLY_FEATURE_TOOLTIP = 'View only. This feature cannot be changed from this page.';

const FEATURE_DISPLAY_ORDER = DEFAULT_PROPERTY_FEATURES.map((feature) => feature.code);

const featureDisplayIndex = (feature: IPropertyFeature) => {
  const index = FEATURE_DISPLAY_ORDER.indexOf(feature.code as Feature);
  return index === -1 ? FEATURE_DISPLAY_ORDER.length : index;
};

const TAX_LINKING_YEARS_OPTIONS: IValueLabel[] = [
  { value: '1', label: '1 Year' },
  { value: '2', label: '2 Years' },
  { value: '3', label: '3 Years' }
];

const VMW_VARIANT_OPTIONS: IValueLabel[] = [
  { value: Feature.VERIFY_MY_WAY, label: 'Professional' },
  { value: Feature.VERIFY_MY_WAY_BASIC, label: 'Basic' }
];

export type PropertyFormProps = {
  defaultValues?: IProperty;
  company?: ICompany | null;
  onSave: (formData: IProperty) => void;
  isCreateForm?: boolean;
  isAdmin?: boolean;
  isFieldDisabled?: boolean;
  propertyFeatures?: IPropertyFeature[];
  isDupDisabled?: boolean;
  owner?: IOwner;
  settingsLink?: string | null;
  areFeaturesReadOnly?: boolean;
};

const PropertyForm: React.FC<PropertyFormProps> = (props: PropertyFormProps) => {
  const {
    defaultValues,
    company,
    onSave,
    isAdmin,
    isCreateForm,
    isFieldDisabled,
    propertyFeatures: initialPropertyFeatures,
    isDupDisabled,
    owner,
    settingsLink,
    areFeaturesReadOnly
  } = props;

  const [propertyFeatures, setPropertyFeatures] = useState<IPropertyFeature[]>(
    initialPropertyFeatures || []
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { SnackTypes, showSnackbar, VariantType } = useSnackbarProvider();

  const settingsServiceCutoverFlag = useFeatureFlag(
    FeatureFlagKey.SNAX_1331_SETTINGS_SERVICE_ENTITY_FEATURES
  );
  const hideFeaturesOnCreate = Boolean(isCreateForm) && settingsServiceCutoverFlag;

  useEffect(() => {
    // This ensures that propertyFeatures in thie component updates whenever they are updated
    // in the parent component. Create+flag-on: stays empty instead of DEFAULT_PROPERTY_FEATURES,
    // so nothing gets written or dual-written.
    if (hideFeaturesOnCreate) {
      setPropertyFeatures([]);
      return;
    }
    setPropertyFeatures(initialPropertyFeatures || DEFAULT_PROPERTY_FEATURES);
  }, [initialPropertyFeatures, hideFeaturesOnCreate]);

  const {
    onSearch: onSearchCompany,
    getAll: companies,
    onResetGetAll: onResetSearchCompany
  } = useSearchCompany();
  const { hideDocumentsTab } = useHideDocLinks();
  const { hidePropertyLinks } = useHidePortalLinks();

  const {
    onSearch: onSearchOwner,
    owners,
    isLoading: isLoadingOwners,
    error: ownersError
  } = useOwnerList();

  const [selectedOwnerName, setSelectedOwnerName] = useState<string | undefined>(
    isCreateForm ? EMPTY_VALUE : owner?.name
  );
  const [isCreateOwnerModalOpen, setIsCreateOwnerModalOpen] = useState(false);

  const { isAdminOrAccountRepresentative } = useUserRoles();
  const isLoading = useSelector(propertySelectors.getOne.isLoading);

  const [searchCompany, setSearchCompany] = useState(EMPTY_VALUE);
  const [searchAddress, setSearchAddress] = useState(EMPTY_VALUE);
  const {
    placeSuggestions,
    isLoading: isPlacesLoading,
    error: placesError,
    formatPlacesSuggestions,
    setSelectedPlace,
    selectedPlace
  } = useGooglePlacesAutocomplete({
    inputValue: searchAddress
  });

  const cpnFraudFF = useFeatureFlag(FeatureFlagKey.CPN_FRAUD);
  const payrollLinkingFeature = propertyFeatures?.find(
    (feature) => feature.code === Feature.PAYROLL_LINKING
  );

  const bankLinkingFeature = propertyFeatures?.find(
    (feature) => feature.code === Feature.BANK_LINKING
  );

  const taxLinkingFeature = propertyFeatures?.find(
    (feature) => feature.code === Feature.TAX_LINKING
  );

  const referenceCompletedNotificationFeature = propertyFeatures?.find(
    (feature) => feature.code === Feature.REFERENCE_COMPLETED_NOTIFICATION
  );

  const identityVerificationFeature = propertyFeatures?.find(
    (feature) => feature.code === Feature.IDENTITY_VERIFICATION
  );

  const identityVerificationOnlyFeature = propertyFeatures?.find(
    (feature, i) => feature.code === Feature.IDENTITY_VERIFICATION_ONLY_WEB_PORTAL
  );

  const cpnFraudCheckFeature = propertyFeatures?.find(
    (feature) => feature.code === Feature.CPN_FRAUD_CHECK
  );

  const verifyMyWayFeature = propertyFeatures?.find(
    (feature) => feature.code === Feature.VERIFY_MY_WAY
  );

  const verifyMyWayBasicFeature = propertyFeatures?.find(
    (feature) => feature.code === Feature.VERIFY_MY_WAY_BASIC
  );

  const onBehalfOfSignatureFeature = propertyFeatures?.find(
    (feature) => feature.code === Feature.ON_BEHALF_OF_SIGNATURE_REQUIRED
  );

  // Applicant-experience bounces the on-behalf-of flow back to the applicant
  // flow when this feature is off, so hide the link rather than send agents there.
  const isOnBehalfOfEnabled =
    propertyFeatures?.find((feature) => feature.code === Feature.ON_BEHALF_OF)?.state ===
    FeatureState.ENABLED;

  const assetVerificationFeature = propertyFeatures?.find(
    (feature) => feature.code === Feature.ASSET_VERIFICATION
  );

  const autoEmploymentVerificationFeature = propertyFeatures?.find(
    (feature) => feature.code === Feature.AUTO_EMPLOYMENT_VERIFICATION
  );

  const autoEmploymentVerificationAlwaysOnFeature = propertyFeatures?.find(
    (feature) => feature.code === Feature.AUTO_EMPLOYMENT_VERIFICATION_ALWAYS_ON
  );

  const showPropertyFeatures = isAdminOrAccountRepresentative && propertyFeatures;

  const methods = useForm<IProperty>({
    defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onSubmit'
  });
  const identityVerificationOptions = new Set(methods.watch(PROPERTY_FIELD.IDV_FLOW_TYPES) || []);
  const isClearProvider = methods.watch('identity_verification_provider') === IDV_PROVIDER.CLEAR;

  const currentIdvValue = methods.watch(PROPERTY_FIELD.IDV_ENABLED);
  const isIdvEnabled =
    identityVerificationFeature?.state === FeatureState.ENABLED ||
    (!identityVerificationFeature && currentIdvValue === true) ||
    (identityVerificationFeature?.state === FeatureState.DISABLED && currentIdvValue === true);

  // The lock mirrors the CPN checkbox's own gate: no visible checkbox, no lock.
  const isCpnFlowTypeLocked =
    isIdvEnabled &&
    isClearProvider &&
    cpnFraudFF &&
    cpnFraudCheckFeature?.state === FeatureState.ENABLED;

  // Writes to form state rather than overriding at render because isDirty compares
  // getValues() to defaultValues; a render-only override would leave Save disabled,
  // so a property carrying a stale flow type could never be corrected.
  useEffect(() => {
    if (!isCpnFlowTypeLocked) return;
    const current = methods.getValues(PROPERTY_FIELD.IDV_FLOW_TYPES) || [];
    if (current.length === 1 && current[0] === IDV_FLOW_TYPE.ENHANCED) return;
    methods.setValue(PROPERTY_FIELD.IDV_FLOW_TYPES, [IDV_FLOW_TYPE.ENHANCED]);
    methods.trigger(PROPERTY_FIELD.IDV_FLOW_TYPES);
  }, [isCpnFlowTypeLocked, methods]);

  // Professional (verify_my_way) wins whenever both are enabled, so the derivation checks
  // it first regardless of which was enabled more recently.
  const activeVmwFeature =
    verifyMyWayFeature?.state === FeatureState.ENABLED
      ? verifyMyWayFeature
      : verifyMyWayBasicFeature?.state === FeatureState.ENABLED
      ? verifyMyWayBasicFeature
      : undefined;
  const isVerifyMyWayChecked = Boolean(activeVmwFeature);
  const vmwVariant: Feature.VERIFY_MY_WAY | Feature.VERIFY_MY_WAY_BASIC =
    activeVmwFeature?.code === Feature.VERIFY_MY_WAY_BASIC
      ? Feature.VERIFY_MY_WAY_BASIC
      : Feature.VERIFY_MY_WAY;

  // Only the two VMW codes are touched, and only their `state`, so an admin's existing
  // verify_my_way config survives a variant switch or an uncheck as a "receipt" of what
  // was previously configured, instead of being wiped like a normal feature disable.
  const setVerifyMyWayState = (
    activeCode: Feature.VERIFY_MY_WAY | Feature.VERIFY_MY_WAY_BASIC | null
  ) => {
    const newPropertyFeatures = propertyFeatures.map((feature) => {
      if (feature.code === Feature.VERIFY_MY_WAY || feature.code === Feature.VERIFY_MY_WAY_BASIC) {
        return {
          ...feature,
          state: feature.code === activeCode ? FeatureState.ENABLED : FeatureState.DISABLED
        };
      }
      return feature;
    });
    setPropertyFeatures(newPropertyFeatures);
    methods.setValue('property_features', newPropertyFeatures);
    methods.trigger('property_features');
  };

  const handleVerifyMyWayCheckboxClick = (isChecked: boolean) => {
    setVerifyMyWayState(isChecked ? Feature.VERIFY_MY_WAY : null);
  };

  // Edge case: both codes loaded as enabled at once — the API now refuses to create this
  // state going forward (property_features.ex enforces Professional-wins on enable), but a
  // row written before that guarantee existed could still load here. Resolves to
  // Professional rather than leaving an ambiguous double-enabled property on screen. Writes
  // to form state, like the CPN flow-type lock above, so isDirty reflects the correction
  // and Save actually persists it.
  useEffect(() => {
    if (
      verifyMyWayFeature?.state !== FeatureState.ENABLED ||
      verifyMyWayBasicFeature?.state !== FeatureState.ENABLED
    ) {
      return;
    }
    setVerifyMyWayState(Feature.VERIFY_MY_WAY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verifyMyWayFeature?.state, verifyMyWayBasicFeature?.state]);

  useEffect(() => {
    if (selectedPlace?.address) {
      methods.setValue('address', selectedPlace?.address.street, { shouldDirty: true });
      methods.setValue('city', selectedPlace?.address.city, { shouldDirty: true });
      methods.setValue('state', selectedPlace?.address.state, { shouldDirty: true });
      methods.setValue('zip', selectedPlace?.address.zipCode, { shouldDirty: true });
      methods.trigger('address');
      methods.trigger('city');
      methods.trigger('state');
      methods.trigger('zip');
    }
  }, [selectedPlace, methods]);

  useEffect(() => {
    if (!isLoading && isSubmitting) {
      setIsSubmitting(false);
    }
  }, [isLoading, isSubmitting]);

  const { getValues } = methods;

  const debouncedSearchCompany = useDebounce(searchCompany, 800);
  useEffect(() => {
    if (debouncedSearchCompany.length) {
      onSearchCompany(debouncedSearchCompany.trim());
    }
  }, [debouncedSearchCompany]);

  const handleOnSave = (formData: IProperty) => {
    if (isSubmitting) {
      return;
    }

    // Only enforced for the roles that can see and persist the flow types. For
    // everyone else the field is hidden and the API strips it, so blocking the
    // save would be an unrecoverable dead-end on a Clear property that already
    // has none set.
    if (
      isAdminOrAccountRepresentative &&
      formData.identity_verification_provider === IDV_PROVIDER.CLEAR &&
      !formData.identity_verification_flow_types?.length
    ) {
      showSnackbar(
        VariantType.error,
        'Please select at least one Identity Verification flow type.',
        SnackTypes.none
      );
      return;
    }

    setIsSubmitting(true);

    // Non-admins use their current company id to create new properties
    if (!isAdmin && company?.short_id) {
      formData.company_short_id = company.short_id;
    }

    const disableIdvWebPortalFeature = (features: IPropertyFeature[]) =>
      features.map((feature) =>
        feature.code === Feature.IDENTITY_VERIFICATION_ONLY_WEB_PORTAL
          ? { ...feature, state: FeatureState.DISABLED }
          : feature
      );

    formData.property_features = propertyFeatures.map((feature) => {
      if (feature.code === Feature.IDENTITY_VERIFICATION) {
        return { ...feature, state: isIdvEnabled ? FeatureState.ENABLED : FeatureState.DISABLED };
      }
      return feature;
    });

    if (!isIdvEnabled) {
      formData.property_features = disableIdvWebPortalFeature(formData.property_features);
    }

    // Company dropdown only selects short_id so this is a hack to get the id.
    const selectedCompany = companies?.data.filter(
      (company) => company.short_id === formData.company_short_id
    )[0];
    formData.company_id = selectedCompany ? selectedCompany?.id : company?.id;
    onSave({ ...formData, phone: formatPhone(formData) });
  };

  const documentTypes = getOldDocumentTypes();

  const propertyShortId = getValues('short_id');
  const isPropertyDisabled =
    defaultValues?.status === 'DISABLED' || defaultValues?.status === 'PENDING';

  const { hasDifferences: isDirty } = getDifferences(defaultValues, getValues(), ['company_id']);

  const identityVerificationProvider = getValues('identity_verification_provider') || EMPTY_VALUE;
  const defaultIdentityVerificationProviders = IDENTITY_VERIFICATION_PROVIDERS.map((provider) => ({
    ...provider,
    isChecked: provider.value === identityVerificationProvider
  }));

  const identityVerificationReportImageTypes =
    getValues('identity_verification_report_image_types') || [];
  const defaultIdentityVerificationReportImageTypes = IDENTITY_VERIFICATION_REPORT_IMAGE_TYPES.map(
    (identityVerificationReportImageType) => {
      if (
        identityVerificationReportImageTypes.length === 0 &&
        identityVerificationReportImageType.value === 'none'
      )
        return { ...identityVerificationReportImageType, isChecked: true };

      return {
        ...identityVerificationReportImageType,
        isChecked:
          identityVerificationReportImageTypes.indexOf(identityVerificationReportImageType.value) >
          -1
      };
    }
  );

  const identityVerificationReportImageTypeOnChange = (value: RadioButtonData[]) => {
    const selected = value.find((item) => item.isChecked);
    if (selected?.value && selected?.value !== 'none') {
      methods.setValue('identity_verification_report_image_types', [selected.value]);
    } else {
      methods.setValue('identity_verification_report_image_types', []);
    }
    methods.trigger('identity_verification_report_image_types');
  };

  const identityVerificationProviderOnChange = (value: RadioButtonData[]) => {
    const selected = value.find((item) => item.isChecked);
    if (selected?.value) {
      methods.setValue('identity_verification_provider', selected.value);
    } else {
      methods.setValue('identity_verification_provider', EMPTY_VALUE);
    }
    methods.trigger('identity_verification_provider');
  };

  const onPropertyFeaturesChange = (isChecked: boolean, featureCode: string) => {
    const state = isChecked ? FeatureState.ENABLED : FeatureState.DISABLED;
    const newPropertyFeatures = propertyFeatures.map((feature, i) => {
      if (
        (feature.code === Feature.PAYROLL_LINKING ||
          feature.code === Feature.BANK_LINKING ||
          feature.code === Feature.TAX_LINKING ||
          feature.code === Feature.VERIFY_MY_WAY ||
          feature.code === Feature.VERIFY_MY_WAY_BASIC ||
          feature.code === Feature.ENHANCED_FINANCIAL ||
          feature.code === Feature.ASSET_VERIFICATION) &&
        featureCode === Feature.INCOME_VERIFICATION &&
        state === FeatureState.DISABLED
      ) {
        return { ...feature, state: FeatureState.DISABLED, config: {} };
      }
      if (
        feature.code === Feature.CPN_FRAUD_CHECK &&
        featureCode === CPN_PARENT_FEATURE &&
        state === FeatureState.DISABLED
      ) {
        return { ...feature, state: FeatureState.DISABLED, config: {} };
      }
      if (
        feature.code === Feature.REFERENCE_COMPLETED_NOTIFICATION &&
        featureCode === Feature.RENTAL_HISTORY &&
        state === FeatureState.DISABLED
      ) {
        return { ...feature, state: FeatureState.DISABLED };
      }
      if (
        feature.code === Feature.IDENTITY_VERIFICATION_ONLY_WEB_PORTAL &&
        featureCode === Feature.IDENTITY_VERIFICATION &&
        state === FeatureState.DISABLED
      ) {
        return { ...feature, state: FeatureState.DISABLED, config: {} };
      }
      if (
        feature.code === Feature.ON_BEHALF_OF_SIGNATURE_REQUIRED &&
        featureCode === Feature.ON_BEHALF_OF &&
        state === FeatureState.DISABLED
      ) {
        return { ...feature, state: FeatureState.DISABLED, config: {} };
      }
      if (feature.code === featureCode) {
        const updated = {
          ...feature,
          state,
          config: state === FeatureState.DISABLED ? {} : feature.config
        };
        if (
          featureCode === Feature.TAX_LINKING &&
          state === FeatureState.ENABLED &&
          !feature.config?.years
        ) {
          updated.config = { ...updated.config, years: DEFAULT_TAX_LINKING_YEARS };
        }
        return updated;
      }
      return feature;
    });
    setPropertyFeatures(newPropertyFeatures);
    methods.setValue('property_features', newPropertyFeatures);
    methods.trigger('property_features');

    if (featureCode === Feature.BANK_LINKING && isChecked) {
      methods.setValue('phone_is_required', true);
      methods.trigger('phone_is_required');
    }
  };

  const onTaxLinkingYearsChange = (years: number) => {
    const newPropertyFeatures = propertyFeatures.map((feature) =>
      feature.code === Feature.TAX_LINKING
        ? { ...feature, config: { ...feature.config, years } }
        : feature
    );
    setPropertyFeatures(newPropertyFeatures);
    methods.setValue('property_features', newPropertyFeatures);
    methods.trigger('property_features');
  };

  const handleIdvCheckboxClick = (isChecked: boolean) => {
    onPropertyFeaturesChange(isChecked, identityVerificationFeature?.code || EMPTY_VALUE);
    methods.setValue(PROPERTY_FIELD.IDV_ENABLED, isChecked);
    methods.trigger(PROPERTY_FIELD.IDV_ENABLED);
  };

  function isPropertyFeatureDisabled(code: string | undefined) {
    if (areFeaturesReadOnly) return true;
    if (!code) return false;
    if (
      code === Feature.OFFER_LETTER_VERIFICATION ||
      code === Feature.ENHANCED_FINANCIAL ||
      code === Feature.AUTO_EMPLOYMENT_VERIFICATION ||
      code === Feature.AUTO_EMPLOYMENT_VERIFICATION_ALWAYS_ON ||
      code === Feature.APPLICATION_TRACKING
    ) {
      return true;
    }
    const propFeats = methods.getValues('property_features') || propertyFeatures;
    if (
      code === Feature.PAYROLL_LINKING ||
      code === Feature.BANK_LINKING ||
      code === Feature.TAX_LINKING ||
      code === Feature.VERIFY_MY_WAY ||
      code === Feature.VERIFY_MY_WAY_BASIC
    ) {
      const ivFeature = propFeats?.find((feature) => feature.code === Feature.INCOME_VERIFICATION);
      return ivFeature?.state === FeatureState.DISABLED;
    }
    if (code === Feature.CPN_FRAUD_CHECK) {
      const parentFeature = propFeats?.find((feature) => feature.code === CPN_PARENT_FEATURE);
      return parentFeature?.state === FeatureState.DISABLED;
    }
    if (code === Feature.REFERENCE_COMPLETED_NOTIFICATION) {
      const rhFeature = propFeats?.find((feature) => feature.code === Feature.RENTAL_HISTORY);
      return rhFeature?.state === FeatureState.DISABLED;
    }
    if (code === Feature.ON_BEHALF_OF_SIGNATURE_REQUIRED) {
      const oboFeature = propFeats?.find((feature) => feature.code === Feature.ON_BEHALF_OF);
      return oboFeature?.state === FeatureState.DISABLED;
    }
    return false;
  }

  // Lives under DUP Customization normally; VMW properties have no DUP, so it moves
  // above the property features instead.
  const applicantInformation = (
    <>
      <Text color={TextColor.secondary} variant={TextVariant.h4}>
        Applicant Information
      </Text>
      <Gap height={1} />

      <Controller
        name="phone_is_required"
        control={methods.control}
        render={({ field: { name, value } }) => {
          // Bank Linking forces phone on. Otherwise read the live form value, not
          // defaultValues, so a remount (VMW toggle) keeps an unsaved toggle.
          const isBankLinkingEnabled = bankLinkingFeature?.state === FeatureState.ENABLED;
          const isPhoneRequired = isBankLinkingEnabled ? true : Boolean(value);

          return (
            <Checkbox
              isChecked={isPhoneRequired}
              value={name}
              name={name}
              onClick={({ isChecked }) => {
                methods.setValue(name, isChecked);
                methods.trigger(name);
              }}
              label="Phone"
              isDisabled={isBankLinkingEnabled}
            />
          );
        }}
      />
      <Gap height={0.5} />

      <Controller
        name="unit_is_required"
        control={methods.control}
        render={({ field: { name, value } }) => (
          <Checkbox
            isChecked={value ?? true}
            value={name}
            name={name}
            onClick={({ isChecked }) => {
              methods.setValue(name, isChecked);
              methods.trigger(name);
            }}
            label="Unit Number (optional)"
          />
        )}
      />
    </>
  );

  return (
    <>
      {company?.short_id &&
        propertyShortId &&
        !isDupDisabled &&
        !hideDocumentsTab &&
        !hidePropertyLinks(propertyFeatures) && (
          <>
            <Row gap={1} alignItems="center">
              <Text>DUP links</Text>
              <Row gap={1.5}>
                <CopyButton
                  name="ApplicationLink"
                  colorLink={ButtonLinkColor.primary}
                  colorCopy={ButtonColor.primary}
                  label="Application Link"
                  to={getPublicLink(
                    propertyShortId || EMPTY_VALUE,
                    company?.short_id || EMPTY_VALUE
                  )}
                  isDisabled={isPropertyDisabled}
                  showCopyButton
                />
                {isOnBehalfOfEnabled && (
                  <CopyButton
                    name="LeasingTeamLink"
                    colorLink={ButtonLinkColor.secondary}
                    colorCopy={ButtonColor.secondary}
                    label="Leasing Team Link"
                    to={getLeasingTeamLink(
                      propertyShortId || EMPTY_VALUE,
                      company?.short_id || EMPTY_VALUE
                    )}
                    isDisabled={isPropertyDisabled}
                  />
                )}
              </Row>
            </Row>
          </>
        )}

      <Gap height={2} />
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(handleOnSave)} noValidate>
          <Container>
            <Row gap={2}>
              <Row.Col size={9}>
                <Row gap={4}>
                  <Row.Col size={1}>
                    {((isCreateForm && isAdmin) || !isCreateForm) && (
                      <CompanyDropdown>
                        <Controller
                          name="company_id"
                          control={methods.control}
                          render={({ field: { name } }) => (
                            <div data-testid="property-company-dropdown">
                              <AutoSuggest
                                name={name}
                                data={
                                  formatLabelValue({
                                    data: companies.data,
                                    value: 'short_id',
                                    label: 'name'
                                  }) as IValueLabel[]
                                }
                                placeholder="Company"
                                initialLabel={isCreateForm ? EMPTY_VALUE : company?.name}
                                onChange={({ value }) => {
                                  setSearchCompany(value.toString());
                                }}
                                onSelect={(item) => {
                                  methods.setValue('company_short_id', item.value);
                                  methods.setValue('company_id', item.value);
                                }}
                                onClear={() => {
                                  onResetSearchCompany();
                                  methods.setValue('company_short_id', EMPTY_VALUE);
                                  methods.setValue('company_id', EMPTY_VALUE);
                                }}
                                isLoading={companies.isLoading}
                                hasError={companies.error !== null}
                                isDisabled={!isCreateForm}
                                config={{
                                  required: 'Complete this field'
                                }}
                              />
                            </div>
                          )}
                        />
                      </CompanyDropdown>
                    )}
                  </Row.Col>
                </Row>
                <Row gap={1}>
                  <Row.Col size={1}>
                    <InputText
                      placeholder="Property Name"
                      name="name"
                      type={InputTextType.text}
                      defaultValue={defaultValues?.name}
                      config={{
                        required: 'Complete this field',
                        minLength: {
                          value: 3,
                          message: 'Property name should have at least three characters'
                        },
                        maxLength: {
                          value: 80,
                          message: 'Property name should have 80 characters maximum'
                        },
                        validate: {
                          noSpecialCharacters: (value) => onlyRequestedSymbols(value)
                        }
                      }}
                      isDisabled={isFieldDisabled}
                    />
                  </Row.Col>
                  <Row.Col size={1}>
                    <InputText
                      className="optional-field"
                      placeholder="Legal Entity Name"
                      name="entity_name"
                      defaultValue={defaultValues?.entity_name}
                      type={InputTextType.text}
                      config={{
                        minLength: {
                          value: 3,
                          message: 'Entity name should have at least three characters'
                        },
                        maxLength: {
                          value: 80,
                          message: 'Entity name should have 80 characters maximum'
                        },
                        validate: {
                          noSpecialCharacters: (value) => validateName(value)
                        }
                      }}
                      isDisabled={isFieldDisabled}
                    />
                  </Row.Col>
                </Row>
                <Row gap={1}>
                  <Row.Col size={0.5}>
                    {isAdminOrAccountRepresentative && (
                      <Controller
                        name="owner_id"
                        control={methods.control}
                        render={({ field: { name } }) => (
                          <div data-testid="property-owner-dropdown">
                            <AutoSuggest
                              name={name}
                              data={
                                formatLabelValue({
                                  data: owners,
                                  value: 'id',
                                  label: 'name'
                                }) as IValueLabel[]
                              }
                              placeholder="Owner"
                              initialLabel={selectedOwnerName}
                              onChange={({ value }) => {
                                onSearchOwner(value.toString());
                              }}
                              onSelect={(item) => {
                                methods.setValue('owner_id', item.value);
                              }}
                              onClear={() => {
                                onSearchOwner(EMPTY_VALUE);
                                methods.setValue('owner_id', EMPTY_VALUE);
                              }}
                              isLoading={isLoadingOwners}
                              hasError={ownersError !== null}
                              isDisabled={false}
                              showOptionalLabel
                              customFirstButton={{
                                label: '+ Create Owner',
                                onCustomClick: () => {
                                  setIsCreateOwnerModalOpen(true);
                                }
                              }}
                            />
                          </div>
                        )}
                      />
                    )}
                  </Row.Col>
                  <Row.Col size={0.25}>
                    <InputText
                      placeholder="Units"
                      name="unit"
                      type={InputTextType.text}
                      defaultValue={defaultValues?.unit?.toString()}
                      config={{
                        required: 'Complete this field',
                        maxLength: { value: 8, message: 'Unit should have 8 characters maximum' },
                        validate: {
                          onlyNumbers: (value) =>
                            onlyNumbersRegex.test(value) ||
                            'Invalid unit. Only numbers are allowed',
                          greaterThanZero: (value) =>
                            value > 0 || 'Unit number should be greater than zero'
                        }
                      }}
                      isDisabled={isFieldDisabled}
                    />
                  </Row.Col>
                </Row>
                <Line />
                <br />
                <Row gap={1}>
                  <Row.Col size={1}>
                    {((isCreateForm && isAdmin) || !isCreateForm) && (
                      <CompanyDropdown>
                        <Controller
                          name="address"
                          control={methods.control}
                          render={({ field: { name } }) => (
                            <div data-testid="property-address-dropdown">
                              <AutoSuggest
                                name={name}
                                data={
                                  getValues('address') && placeSuggestions.length === 0
                                    ? [
                                        {
                                          data: getValues('address'),
                                          value: getValues('address'),
                                          label: getValues('address')
                                        } as IValueLabel
                                      ]
                                    : formatPlacesSuggestions(placeSuggestions)
                                }
                                placeholder="Street Address"
                                initialLabel={isCreateForm ? EMPTY_VALUE : defaultValues?.address}
                                onChange={({ value }) => {
                                  setSearchAddress(value.toString());
                                }}
                                onSelect={(item) => {
                                  methods.setValue('address', item.value);
                                  setSelectedPlace({ placeId: item?.metadata?.placeId as string });
                                }}
                                onClear={() => {
                                  methods.setValue('address', EMPTY_VALUE);
                                  methods.setValue('city', EMPTY_VALUE);
                                  methods.setValue('state', EMPTY_VALUE);
                                  methods.setValue('zip', EMPTY_VALUE);
                                  setSearchAddress(EMPTY_VALUE);
                                }}
                                isLoading={isPlacesLoading}
                                hasError={placesError !== null}
                                config={{
                                  required: 'Complete this field'
                                }}
                                setLabelToValue
                              />
                            </div>
                          )}
                        />
                      </CompanyDropdown>
                    )}
                  </Row.Col>
                </Row>
                <Row gap={1}>
                  <Row.Col size={1}>
                    <InputText
                      placeholder="City"
                      name="city"
                      defaultValue={defaultValues?.city}
                      type={InputTextType.text}
                      config={{
                        required: 'Complete this field',
                        maxLength: {
                          value: 80,
                          message: 'City name should have 80 characters maximum'
                        }
                      }}
                      isDisabled={isFieldDisabled}
                    />
                  </Row.Col>
                  <Row.Col size={1}>
                    <ControlledDropdown
                      showLabel
                      placeholder="State"
                      name="state"
                      data={STATES}
                      config={{ required: 'Complete this field' }}
                      size="md"
                      isDisabled={isFieldDisabled}
                    />
                  </Row.Col>
                </Row>
                <Row gap={1}>
                  <Row.Col size={1}>
                    <InputText
                      placeholder="ZIP Code"
                      name="zip"
                      defaultValue={defaultValues?.zip}
                      type={InputTextType.text}
                      config={{
                        required: 'Complete this field',
                        validate: (value) => usZipCodeRegex.test(value) || 'Invalid zip code'
                      }}
                      isDisabled={isFieldDisabled}
                    />
                  </Row.Col>
                  <Row.Col size={1}>
                    <InputText
                      placeholder="Contact Email"
                      name="email"
                      defaultValue={defaultValues?.email}
                      type={InputTextType.email}
                      config={{
                        required: 'Complete this field'
                      }}
                      isDisabled={isFieldDisabled}
                    />
                  </Row.Col>
                </Row>
                {isAdminOrAccountRepresentative && (
                  <Row gap={1}>
                    <Row.Col size={1}>
                      <InputText
                        placeholder="Salesforce ID"
                        name="sfdc_id"
                        defaultValue={defaultValues?.sfdc_id || undefined}
                        type={InputTextType.text}
                        config={{
                          required: 'Complete this field',
                          validate: {
                            validSalesforceId: (value) =>
                              !value ||
                              isValidSalesforceId(value) ||
                              'Must be a valid 15 or 18 character Salesforce ID'
                          }
                        }}
                        isDisabled={isFieldDisabled}
                      />
                    </Row.Col>
                    <Row.Col size={1}>
                      <InputText
                        placeholder="Property ID"
                        name="short_id"
                        defaultValue={defaultValues?.short_id}
                        type={InputTextType.text}
                        isReadonly
                      />
                    </Row.Col>
                  </Row>
                )}
                <Row gap={1}>
                  <Row.Col size={1}>
                    <InputText
                      className="optional-field"
                      placeholder="Customer Property Code"
                      name="customer_property_code"
                      defaultValue={defaultValues?.customer_property_code ?? undefined}
                      type={InputTextType.text}
                      config={{
                        maxLength: {
                          value: 120,
                          message: 'Customer Property Code should have 120 characters maximum'
                        },
                        validate: {
                          noWhiteSpaces: (value) =>
                            value
                              ? noWhiteSpacesRegex.test(value) ||
                                'Customer Property Code should not start or end with whitespaces'
                              : undefined
                        }
                      }}
                      isDisabled={isFieldDisabled}
                    />
                  </Row.Col>
                  <Row.Col size={1}>
                    <InputText
                      className="optional-field"
                      placeholder="Phone"
                      name="phone"
                      defaultValue={defaultValues?.phone}
                      type={InputTextType.tel}
                      isDisabled={isFieldDisabled}
                      config={{
                        required: 'Complete this field'
                      }}
                    />
                  </Row.Col>
                </Row>
                {isVerifyMyWayChecked && (
                  <>
                    <Gap height={1} />
                    <Row>
                      <Row.Col size={1}>{applicantInformation}</Row.Col>
                    </Row>
                    <Gap height={1} />
                    <Line />
                    <Gap height={1} />
                  </>
                )}
                {!hideFeaturesOnCreate && (
                  <>
                    {isAdmin && areFeaturesReadOnly && (
                      <>
                        <Row>
                          <Row.Col>
                            <Text variant={TextVariant.small} color={TextColor.gray700}>
                              Property features are now managed in the Settings Service.
                            </Text>
                          </Row.Col>
                        </Row>
                        {settingsLink && (
                          <>
                            <Gap height={0.5} />
                            <Row>
                              <Row.Col>
                                <ButtonLink
                                  name="settings-service-link"
                                  label="Settings Service"
                                  to={settingsLink}
                                  isExternal
                                  target={ButtonLinkTarget.blank}
                                  variant={ButtonLinkVariant.outline}
                                  color={ButtonLinkColor.primary}
                                  size={ButtonLinkSize.normal}
                                >
                                  Settings Service&nbsp;&nbsp;
                                  <Icon icon="launch" />
                                </ButtonLink>
                              </Row.Col>
                            </Row>
                          </>
                        )}
                        <Gap height={0.5} />
                      </>
                    )}
                    {isAdminOrAccountRepresentative && (
                      <Row>
                        <Row.Col>
                          <Checkbox
                            isChecked={isIdvEnabled}
                            name={PROPERTY_FIELD.IDV_ENABLED}
                            value={PROPERTY_FIELD.IDV_ENABLED}
                            onClick={({ isChecked }) => handleIdvCheckboxClick(isChecked)}
                            label="Identity Verification"
                            isDisabled={areFeaturesReadOnly}
                          />
                        </Row.Col>
                      </Row>
                    )}
                    {isIdvEnabled && isAdminOrAccountRepresentative && (
                      <>
                        <Gap height={0.5} />
                        <Row marginLeft="24px">
                          <Row.Col>
                            <Checkbox
                              isChecked={
                                identityVerificationOnlyFeature?.state === FeatureState.ENABLED
                              }
                              name={identityVerificationOnlyFeature?.name || EMPTY_VALUE}
                              value={identityVerificationOnlyFeature?.code || EMPTY_VALUE}
                              onClick={({ isChecked }) => {
                                onPropertyFeaturesChange(
                                  isChecked,
                                  identityVerificationOnlyFeature?.code || EMPTY_VALUE
                                );
                              }}
                              label={identityVerificationOnlyFeature?.name || EMPTY_VALUE}
                              isDisabled={areFeaturesReadOnly}
                            />
                          </Row.Col>
                        </Row>
                      </>
                    )}
                    {/* IDV settings are Snappt-only */}
                    {isIdvEnabled && isAdminOrAccountRepresentative && (
                      <>
                        <Gap height={0.5} />
                        <Row gap={1} marginLeft="30px">
                          <Row.Col>
                            <Text variant={TextVariant.small}>Identity Verification Provider:</Text>
                          </Row.Col>
                          <Row.Col>
                            <RadioButtonGroup
                              name="identity_verification_provider"
                              data={defaultIdentityVerificationProviders}
                              className="identityVerificationProviderRadioButtonGroup"
                              onChange={identityVerificationProviderOnChange}
                            />
                          </Row.Col>
                        </Row>
                        {isAdminOrAccountRepresentative && cpnFraudFF && isClearProvider && (
                          <>
                            <Gap height={0.5} />
                            <Row marginLeft="24px">
                              <Row.Col>
                                <Checkbox
                                  isChecked={cpnFraudCheckFeature?.state === FeatureState.ENABLED}
                                  name={cpnFraudCheckFeature?.name || EMPTY_VALUE}
                                  value={cpnFraudCheckFeature?.code || EMPTY_VALUE}
                                  onClick={({ isChecked }) => {
                                    onPropertyFeaturesChange(
                                      isChecked,
                                      cpnFraudCheckFeature?.code || EMPTY_VALUE
                                    );
                                  }}
                                  label="CPN Fraud Check"
                                  isDisabled={isPropertyFeatureDisabled(cpnFraudCheckFeature?.code)}
                                />
                              </Row.Col>
                            </Row>
                          </>
                        )}
                        {isIdvEnabled && isClearProvider && (
                          <>
                            <Gap height={0.5} />
                            <Row gap={1} marginLeft="30px" alignItems="center">
                              <Row.Col>
                                <Text variant={TextVariant.small}>
                                  Identity Verification Flow Types:
                                </Text>
                              </Row.Col>
                              <Row.Col>
                                <Row gap={0.5}>
                                  {IDV_FLOW_TYPES_CHECKBOXES.map(({ value, label, name }) => (
                                    <Checkbox
                                      key={value}
                                      isChecked={identityVerificationOptions.has(value)}
                                      name={name}
                                      value={value}
                                      label={label}
                                      isDisabled={isCpnFlowTypeLocked}
                                      onClick={({ isChecked }) => {
                                        const updated = new Set(identityVerificationOptions);
                                        isChecked ? updated.add(value) : updated.delete(value);
                                        methods.setValue(
                                          PROPERTY_FIELD.IDV_FLOW_TYPES,
                                          Array.from(updated)
                                        );
                                        methods.trigger(PROPERTY_FIELD.IDV_FLOW_TYPES);
                                      }}
                                    />
                                  ))}
                                </Row>
                              </Row.Col>
                            </Row>
                          </>
                        )}
                        <Gap height={0.5} />
                        <Row gap={1} marginLeft="30px">
                          <Row.Col>
                            <Text variant={TextVariant.small}>
                              Identity Verification Report Image Type:
                            </Text>
                          </Row.Col>
                          <Row.Col>
                            <RadioButtonGroup
                              name="identity_verification_report_image_types"
                              data={defaultIdentityVerificationReportImageTypes}
                              className="identityVerificationReportImageTypesRadioButtonGroup"
                              onChange={identityVerificationReportImageTypeOnChange}
                            />
                          </Row.Col>
                        </Row>
                      </>
                    )}
                    {showPropertyFeatures &&
                      [...propertyFeatures]
                        .sort((a, b) => featureDisplayIndex(a) - featureDisplayIndex(b))
                        .map((feature, i) => {
                          switch (feature.code) {
                            case Feature.PAYROLL_LINKING:
                            case Feature.BANK_LINKING:
                            case Feature.TAX_LINKING:
                            case Feature.REFERENCE_COMPLETED_NOTIFICATION:
                            case Feature.IDENTITY_VERIFICATION:
                            case Feature.IDENTITY_VERIFICATION_ONLY_WEB_PORTAL:
                            case Feature.VERIFY_MY_WAY:
                            case Feature.VERIFY_MY_WAY_BASIC:
                            case Feature.CPN_FRAUD_CHECK:
                            case Feature.ASSET_VERIFICATION:
                            case Feature.ON_BEHALF_OF_SIGNATURE_REQUIRED:
                            case Feature.AUTO_EMPLOYMENT_VERIFICATION:
                            case Feature.AUTO_EMPLOYMENT_VERIFICATION_ALWAYS_ON:
                              return null;
                            default:
                              break;
                          }

                          return (
                            <Fragment key={feature.code || i}>
                              <Gap height={0.5} />
                              <Row key={i}>
                                <Row.Col>
                                  <Checkbox
                                    isChecked={feature.state === FeatureState.ENABLED}
                                    name={feature.name || EMPTY_VALUE}
                                    value={feature.code || EMPTY_VALUE}
                                    onClick={({ isChecked }) => {
                                      onPropertyFeaturesChange(
                                        isChecked,
                                        feature.code || EMPTY_VALUE
                                      );
                                    }}
                                    label={featureLabel(feature)}
                                    isDisabled={isPropertyFeatureDisabled(feature?.code)}
                                    title={
                                      feature.code === Feature.OFFER_LETTER_VERIFICATION ||
                                      feature.code === Feature.ENHANCED_FINANCIAL ||
                                      feature.code === Feature.APPLICATION_TRACKING
                                        ? READ_ONLY_FEATURE_TOOLTIP
                                        : undefined
                                    }
                                  />
                                </Row.Col>
                              </Row>
                              {feature.code === Feature.OFFER_LETTER_VERIFICATION &&
                                feature.state === FeatureState.ENABLED && (
                                  <>
                                    <Gap height={0.5} />
                                    <Row gap={1} marginLeft="28px">
                                      <Row.Col>
                                        <Checkbox
                                          isChecked={
                                            autoEmploymentVerificationFeature?.state ===
                                            FeatureState.ENABLED
                                          }
                                          name={
                                            autoEmploymentVerificationFeature?.name || EMPTY_VALUE
                                          }
                                          value={
                                            autoEmploymentVerificationFeature?.code || EMPTY_VALUE
                                          }
                                          onClick={({ isChecked }) => {
                                            onPropertyFeaturesChange(
                                              isChecked,
                                              autoEmploymentVerificationFeature?.code || EMPTY_VALUE
                                            );
                                          }}
                                          label="Auto VOE: No Valid Income"
                                          isDisabled={isPropertyFeatureDisabled(
                                            autoEmploymentVerificationFeature?.code
                                          )}
                                          title={READ_ONLY_FEATURE_TOOLTIP}
                                        />
                                      </Row.Col>
                                    </Row>
                                    <Row gap={1} marginLeft="28px">
                                      <Row.Col>
                                        <Checkbox
                                          isChecked={
                                            autoEmploymentVerificationAlwaysOnFeature?.state ===
                                            FeatureState.ENABLED
                                          }
                                          name={
                                            autoEmploymentVerificationAlwaysOnFeature?.name ||
                                            EMPTY_VALUE
                                          }
                                          value={
                                            autoEmploymentVerificationAlwaysOnFeature?.code ||
                                            EMPTY_VALUE
                                          }
                                          onClick={({ isChecked }) => {
                                            onPropertyFeaturesChange(
                                              isChecked,
                                              autoEmploymentVerificationAlwaysOnFeature?.code ||
                                                EMPTY_VALUE
                                            );
                                          }}
                                          label="Auto VOE: All Offer Letters"
                                          isDisabled={isPropertyFeatureDisabled(
                                            autoEmploymentVerificationAlwaysOnFeature?.code
                                          )}
                                          title={READ_ONLY_FEATURE_TOOLTIP}
                                        />
                                      </Row.Col>
                                    </Row>
                                  </>
                                )}
                              {feature.code === Feature.INCOME_VERIFICATION &&
                                feature.state === FeatureState.ENABLED && (
                                  <>
                                    <Gap height={0.5} />
                                    <Row gap={1} marginLeft="28px">
                                      <Row.Col>
                                        <Checkbox
                                          isChecked={
                                            payrollLinkingFeature?.state === FeatureState.ENABLED
                                          }
                                          name={payrollLinkingFeature?.name || EMPTY_VALUE}
                                          value={payrollLinkingFeature?.code || EMPTY_VALUE}
                                          onClick={({ isChecked }) => {
                                            onPropertyFeaturesChange(
                                              isChecked,
                                              payrollLinkingFeature?.code || EMPTY_VALUE
                                            );
                                          }}
                                          label="Connected Payroll"
                                          isDisabled={isPropertyFeatureDisabled(
                                            payrollLinkingFeature?.code
                                          )}
                                        />
                                      </Row.Col>
                                    </Row>
                                    <Row gap={1} marginLeft="28px">
                                      <Row.Col>
                                        <Checkbox
                                          isChecked={
                                            bankLinkingFeature?.state === FeatureState.ENABLED
                                          }
                                          name={bankLinkingFeature?.name || EMPTY_VALUE}
                                          value={bankLinkingFeature?.code || EMPTY_VALUE}
                                          onClick={({ isChecked }) => {
                                            onPropertyFeaturesChange(
                                              isChecked,
                                              bankLinkingFeature?.code || EMPTY_VALUE
                                            );
                                          }}
                                          label="Bank Linking"
                                          isDisabled={isPropertyFeatureDisabled(
                                            bankLinkingFeature?.code
                                          )}
                                        />
                                      </Row.Col>
                                    </Row>
                                    <Row gap={1} marginLeft="28px">
                                      <Row.Col>
                                        <Checkbox
                                          isChecked={
                                            taxLinkingFeature?.state === FeatureState.ENABLED
                                          }
                                          name={taxLinkingFeature?.name || EMPTY_VALUE}
                                          value={taxLinkingFeature?.code || EMPTY_VALUE}
                                          onClick={({ isChecked }) => {
                                            onPropertyFeaturesChange(
                                              isChecked,
                                              taxLinkingFeature?.code || EMPTY_VALUE
                                            );
                                          }}
                                          label="Tax Linking"
                                          isDisabled={isPropertyFeatureDisabled(
                                            taxLinkingFeature?.code
                                          )}
                                        />
                                      </Row.Col>
                                    </Row>
                                    {taxLinkingFeature?.state === FeatureState.ENABLED && (
                                      <>
                                        <Gap height={0.5} />
                                        <Row
                                          gap={1}
                                          marginLeft="56px"
                                          data-testid="tax-linking-years-dropdown"
                                        >
                                          <Row.Col size={0.25}>
                                            <Dropdown
                                              name="tax_linking_years"
                                              placeholder="Years"
                                              data={TAX_LINKING_YEARS_OPTIONS}
                                              valueInitial={String(
                                                taxLinkingFeature?.config?.years ??
                                                  DEFAULT_TAX_LINKING_YEARS
                                              )}
                                              size="sm"
                                              showLabel
                                              isDisabled={areFeaturesReadOnly}
                                              onChange={({ tax_linking_years: { selected } }) => {
                                                onTaxLinkingYearsChange(Number(selected.value));
                                              }}
                                            />
                                          </Row.Col>
                                        </Row>
                                      </>
                                    )}
                                    <Row gap={1} marginLeft="28px" alignItems="center">
                                      <Row.Col>
                                        <Checkbox
                                          isChecked={isVerifyMyWayChecked}
                                          name={verifyMyWayFeature?.name || 'Verify My Way'}
                                          value={Feature.VERIFY_MY_WAY}
                                          onClick={({ isChecked }) => {
                                            handleVerifyMyWayCheckboxClick(isChecked);
                                          }}
                                          label="Verify My Way"
                                          isDisabled={isPropertyFeatureDisabled(
                                            verifyMyWayFeature?.code
                                          )}
                                        />
                                      </Row.Col>
                                      {isVerifyMyWayChecked && (
                                        <Row.Col
                                          size={0.5}
                                          data-testid="verify-my-way-variant-dropdown"
                                        >
                                          <Dropdown
                                            name="verify_my_way_variant"
                                            placeholder="Tier"
                                            data={VMW_VARIANT_OPTIONS}
                                            valueInitial={vmwVariant}
                                            size="sm"
                                            showLabel
                                            isDisabled={areFeaturesReadOnly}
                                            onChange={({ verify_my_way_variant: { selected } }) => {
                                              setVerifyMyWayState(
                                                selected.value as
                                                  | Feature.VERIFY_MY_WAY
                                                  | Feature.VERIFY_MY_WAY_BASIC
                                              );
                                            }}
                                          />
                                        </Row.Col>
                                      )}
                                    </Row>
                                    <Row gap={1} marginLeft="28px">
                                      <Row.Col>
                                        <Checkbox
                                          isChecked={
                                            assetVerificationFeature?.state === FeatureState.ENABLED
                                          }
                                          name={assetVerificationFeature?.name || EMPTY_VALUE}
                                          value={assetVerificationFeature?.code || EMPTY_VALUE}
                                          onClick={({ isChecked }) => {
                                            onPropertyFeaturesChange(
                                              isChecked,
                                              assetVerificationFeature?.code || EMPTY_VALUE
                                            );
                                          }}
                                          label="VOA"
                                          isDisabled={isPropertyFeatureDisabled(
                                            assetVerificationFeature?.code
                                          )}
                                        />
                                      </Row.Col>
                                    </Row>
                                  </>
                                )}
                              {feature.code === Feature.RENTAL_HISTORY &&
                                feature.state === FeatureState.ENABLED && (
                                  <>
                                    <Gap height={0.5} />
                                    <Row gap={1} marginLeft="28px">
                                      <Row.Col>
                                        <Checkbox
                                          isChecked={
                                            referenceCompletedNotificationFeature?.state ===
                                            FeatureState.ENABLED
                                          }
                                          name={
                                            referenceCompletedNotificationFeature?.name ||
                                            EMPTY_VALUE
                                          }
                                          value={
                                            referenceCompletedNotificationFeature?.code ||
                                            EMPTY_VALUE
                                          }
                                          onClick={({ isChecked }) => {
                                            onPropertyFeaturesChange(
                                              isChecked,
                                              referenceCompletedNotificationFeature?.code ||
                                                EMPTY_VALUE
                                            );
                                          }}
                                          label="Reference Completed Notification"
                                          isDisabled={isPropertyFeatureDisabled(
                                            referenceCompletedNotificationFeature?.code
                                          )}
                                        />
                                      </Row.Col>
                                    </Row>
                                  </>
                                )}
                              {feature.code === Feature.ON_BEHALF_OF &&
                                feature.state === FeatureState.ENABLED && (
                                  <>
                                    <Gap height={0.5} />
                                    <Row gap={1} marginLeft="28px">
                                      <Row.Col>
                                        <Checkbox
                                          isChecked={
                                            onBehalfOfSignatureFeature?.state ===
                                            FeatureState.ENABLED
                                          }
                                          name={onBehalfOfSignatureFeature?.name || EMPTY_VALUE}
                                          value={onBehalfOfSignatureFeature?.code || EMPTY_VALUE}
                                          onClick={({ isChecked }) => {
                                            onPropertyFeaturesChange(
                                              isChecked,
                                              onBehalfOfSignatureFeature?.code || EMPTY_VALUE
                                            );
                                          }}
                                          label="On Behalf Of Signature Required"
                                          isDisabled={isPropertyFeatureDisabled(
                                            onBehalfOfSignatureFeature?.code
                                          )}
                                        />
                                      </Row.Col>
                                    </Row>
                                  </>
                                )}
                            </Fragment>
                          );
                        })}
                  </>
                )}
                {!isVerifyMyWayChecked && (
                  <>
                    <Gap height={1} />
                    <Line />
                    <Gap height={2} />
                    <Row>
                      <Row.Col size={2}>
                        <Text variant={TextVariant.h3}>Document Upload Portal Customization</Text>
                        <Gap height={0.5} />
                        <Text variant={TextVariant.normal} color={TextColor.gray700}>
                          This customization will be reflected in the DUP&apos;s form fields.
                        </Text>
                        <Gap height={1.5} />
                      </Row.Col>
                    </Row>
                    <Row>
                      <Row.Col size={1}>
                        <Gap height={1} />
                        {applicantInformation}
                      </Row.Col>
                      <Row.Col size={1}>
                        <Gap height={1} />
                        <Text color={TextColor.secondary} variant={TextVariant.h4}>
                          Document Uploads
                        </Text>
                        {documentTypes.map(({ key, label }) => (
                          <Fragment key={key}>
                            <Gap height={1} />
                            <Text color={TextColor.secondary}>{label} Required</Text>
                            <Gap />
                            <Controller
                              name={`supported_doctypes.${key as keyof IPropertySupportedDoctypes}`}
                              control={methods.control}
                              render={({ field: { name } }) => (
                                <InputNumber
                                  value={
                                    defaultValues?.supported_doctypes &&
                                    defaultValues?.supported_doctypes[
                                      key as keyof IPropertySupportedDoctypes
                                    ]
                                      ? (defaultValues?.supported_doctypes[
                                          key as keyof IPropertySupportedDoctypes
                                        ] as number)
                                      : 0
                                  }
                                  name={name}
                                  onChange={(value) => {
                                    methods.setValue(name, value);
                                    methods.trigger(name);
                                  }}
                                />
                              )}
                            />
                          </Fragment>
                        ))}
                      </Row.Col>
                    </Row>
                  </>
                )}
                <Row>
                  <Row.Col size={2}>
                    <Gap height={2} />
                    <Line />
                    <Gap height={2} />
                  </Row.Col>
                </Row>
              </Row.Col>
              <Row.Col size={3}>
                <FileInputField name="logo" title="Property Logo" isDisabled={isFieldDisabled} />
              </Row.Col>
            </Row>
            <Row gap={1}>
              <Button
                className="save-button"
                name="SubmitPropertyDetails"
                variant={ButtonVariant.contained}
                isDisabled={!isDirty || isLoading || isSubmitting}
                isLoading={isLoading}
                type={ButtonType.submit}
              >
                {isLoading ? 'Saving' : 'Save'}
              </Button>
              <ButtonLink name="Cancel" variant={ButtonLinkVariant.outline} to="/properties">
                Cancel
              </ButtonLink>
            </Row>
          </Container>
        </form>
      </FormProvider>
      <CreateOwnerModal
        isOpen={isCreateOwnerModalOpen}
        closeModal={(payload) => {
          const { ownerId, ownerName } = payload || {};
          // Do something with ownerId and ownerName if needed
          if (ownerId && ownerName) {
            methods.setValue('owner_id', ownerId);
            setSelectedOwnerName(ownerName);
          }
          setIsCreateOwnerModalOpen(false);
        }}
      />
    </>
  );
};

PropertyForm.defaultProps = {
  defaultValues: {
    address: EMPTY_VALUE,
    city: EMPTY_VALUE,
    state: EMPTY_VALUE,
    zip: EMPTY_VALUE,
    status: PropertyStatus.ACTIVE,
    unit: 1,
    company_short_id: EMPTY_VALUE,
    name: EMPTY_VALUE,
    sfdc_id: EMPTY_VALUE,
    property_features: [],
    phone_is_required: true,
    unit_is_required: true,
    supported_doctypes: {
      paystub: 2,
      bank_statement: 2,
      cash_app_statement: 0,
      credit_debit_card_statement: 0,
      department_of_veterans_affairs_benefit_letter: 0,
      investment_account: 0,
      social_security_benefits_letter: 0,
      social_security_statement: 0,
      tax_transcript: 0,
      utility_bill: 0
    },
    identity_verification_report_image_types: [],
    identity_verification_provider: EMPTY_VALUE,
    identity_verification_flow_types: [IDV_FLOW_TYPE.BASIC, IDV_FLOW_TYPE.ENHANCED]
  },
  onSave: () => null,
  isCreateForm: false,
  isAdmin: true,
  isFieldDisabled: false
};

export default PropertyForm;
